import React, { useState } from 'react';

export default function DnsRecordsPanel({
  records,
  includeSpf = false,
  verifyButton = null,
  onCopy,
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  if (!records) return null;

  const returnPathHost = records.returnPathHost || 'pm-bounces';
  const returnPathValue = records.returnPathValue || 'pm.mtasv.net';

  function copy(text) {
    navigator.clipboard.writeText(text)
      .then(() => onCopy?.('Copied!'))
      .catch(() => onCopy?.('Copy failed', 'error'));
  }

  return (
    <div className="domains-dns-panel">
      <h3 className="domains-dns-step-title" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
        Add These 2 DNS Records
      </h3>
      <p className="domains-dns-intro">
        Add these records to your DNS provider (Cloudflare, GoDaddy, etc.). Verification usually takes 5-15 minutes.
      </p>

      <div className="fb-callout success" style={{ marginBottom: '1.25rem' }}>
        <strong>Won&apos;t interfere with your email</strong>
        <p style={{ fontSize: '0.85rem', margin: '0.5rem 0 0', lineHeight: 1.5 }}>
          These records only affect <strong>outbound</strong> emails. Your existing email provider (Gmail, etc.) will continue working normally for receiving emails.
        </p>
      </div>

      {records.dkimHost && records.dkimValue && (
        <div className="domains-dns-step">
          <div className="domains-dns-step-header">
            <span className="inbox-chip accent-soft">Required</span>
            <h4 className="domains-dns-step-title">1. DKIM Record (TXT)</h4>
          </div>
          <div className="domains-dns-field">
            <label className="domains-field-label">Host/Name</label>
            <div className="domains-dns-copy-row">
              <input type="text" className="fb-input" readOnly value={records.dkimHost} />
              <button type="button" className="inbox-pill-btn" onClick={() => copy(records.dkimHost)}>Copy</button>
            </div>
          </div>
          <div className="domains-dns-field">
            <label className="domains-field-label">Value/Content</label>
            <div className="domains-dns-copy-row">
              <textarea className="fb-textarea" readOnly value={records.dkimValue} rows={4} />
              <button type="button" className="inbox-pill-btn" onClick={() => copy(records.dkimValue)}>Copy</button>
            </div>
          </div>
          <div className="fb-callout info" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
            <strong>In your DNS provider:</strong> Add a new <code className="fb-code">TXT</code> record with the values above.
          </div>
        </div>
      )}

      <div className="domains-dns-step">
        <div className="domains-dns-step-header">
          <span className="inbox-chip accent-soft">Required</span>
          <h4 className="domains-dns-step-title">2. Return-Path (CNAME)</h4>
        </div>
        <div className="fb-callout warn" style={{ marginBottom: '0.75rem' }}>
          <strong>Cloudflare users</strong>
          <p style={{ fontSize: '0.8rem', margin: '0.5rem 0 0', lineHeight: 1.5 }}>
            After adding this CNAME record, <strong>disable the proxy</strong> by clicking the orange cloud icon to make it gray (DNS only).
          </p>
        </div>
        <div className="domains-dns-field">
          <label className="domains-field-label">Host/Name</label>
          <div className="domains-dns-copy-row">
            <input type="text" className="fb-input" readOnly value={returnPathHost} />
            <button type="button" className="inbox-pill-btn" onClick={() => copy(returnPathHost)}>Copy</button>
          </div>
        </div>
        <div className="domains-dns-field">
          <label className="domains-field-label">Value/Target</label>
          <div className="domains-dns-copy-row">
            <input type="text" className="fb-input" readOnly value={returnPathValue} />
            <button type="button" className="inbox-pill-btn" onClick={() => copy(returnPathValue)}>Copy</button>
          </div>
        </div>
        <div className="fb-callout info" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
          <strong>In your DNS provider:</strong> Add a new <code className="fb-code">CNAME</code> record with the values above.
        </div>
      </div>

      {verifyButton}

      {includeSpf && records.spf && (
        <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--hairline-2)' }}>
          <button
            type="button"
            className="inbox-pill-btn domains-advanced-toggle"
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            <span>Advanced Settings (SPF Record - Optional)</span>
            <span>{showAdvanced ? '▼' : '▶'}</span>
          </button>
          {showAdvanced && (
            <div className="today-panel" style={{ marginTop: '1rem' }}>
              <div className="today-panel-body">
                <p className="domains-dns-intro">
                  <strong>Note:</strong> Postmark handles SPF automatically. Only add this if you need to combine with an existing SPF record.
                </p>
                <div className="domains-dns-field">
                  <label className="domains-field-label">SPF Record Value</label>
                  <div className="domains-dns-copy-row">
                    <input type="text" className="fb-input" readOnly value={records.spf} />
                    <button type="button" className="inbox-pill-btn" onClick={() => copy(records.spf)}>Copy</button>
                  </div>
                </div>
                <div className="fb-callout warn" style={{ marginBottom: 0 }}>
                  <strong>If you have an existing SPF record:</strong> Edit it to include <code className="fb-code">include:spf.postmarkapp.com</code>. Don&apos;t create a second SPF record.
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
